const BASE = import.meta.env.VITE_API_BASE || '';

const ACCESS_KEY = 'vpnsaler_access';
const REFRESH_KEY = 'vpnsaler_refresh';

export const tokenStore = {
  get access() {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refresh() {
    return localStorage.getItem(REFRESH_KEY);
  },
  set(access: string, refresh?: string | null) {
    localStorage.setItem(ACCESS_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

async function request<T>(path: string, init: RequestInit = {}, opts: { auth?: boolean } = { auth: true }): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((init.headers as Record<string, string>) || {}),
  };
  if (opts.auth && tokenStore.access) {
    headers['Authorization'] = `Bearer ${tokenStore.access}`;
  }

  const res = await fetch(`${BASE}${path}`, { ...init, headers });
  if (res.status === 401 && opts.auth && tokenStore.refresh) {
    // try single refresh
    const ok = await tryRefresh();
    if (ok) {
      headers['Authorization'] = `Bearer ${tokenStore.access}`;
      const retry = await fetch(`${BASE}${path}`, { ...init, headers });
      if (!retry.ok) throw new Error(`API ${retry.status}: ${await retry.text()}`);
      return retry.json();
    }
    tokenStore.clear();
  }
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API ${res.status}: ${text || res.statusText}`);
  }
  return res.json();
}

async function tryRefresh(): Promise<boolean> {
  const refresh = tokenStore.refresh;
  if (!refresh) return false;
  try {
    const res = await fetch(`${BASE}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh }),
    });
    if (!res.ok) return false;
    const data = (await res.json()) as AuthOut;
    tokenStore.set(data.access, data.refresh ?? null);
    return true;
  } catch {
    return false;
  }
}

// ---------- Types ----------

export interface Plan {
  code: string;
  days: number;
  price: number;
  per_day: string;
  label: string;
  popular: boolean;
}

export interface User {
  id: number;
  tg_id: number | null;
  username: string | null;
  email: string | null;
  name: string | null;
  email_verified: boolean;
  created_at: string;
}

export type Protocol = 'wireguard' | 'vless';
export type SubStatus = 'pending' | 'active' | 'expired' | 'disabled';

export interface Subscription {
  id: number;
  protocol: Protocol;
  status: SubStatus;
  device_label: string | null;
  plan_code: string | null;
  plan_label: string | null;
  total_days: number | null;
  days_left: number;
  active_since: string | null;
  expires_at: string | null;
}

export type PayStatus = 'pending' | 'paid' | 'failed' | 'trial';

export interface Payment {
  id: number;
  order_id: string;
  plan_label: string;
  protocol: Protocol | null;
  amount: number;
  status: PayStatus;
  created_at: string;
}

export interface ReferralStats {
  code: string;
  link: string;
  invited_count: number;
  paid_count: number;
  bonus_days_received: number;
}

export interface AuthOut {
  access: string;
  refresh?: string;
  user: User;
}

export interface CreateOrderIn {
  plan_code: string;
  protocol: Protocol;
  device_label?: string;
}

export interface CreateOrderOut {
  order_id: string;
  amount: number;
  currency?: string;
  status: PayStatus;
  confirmation_url: string | null;
  auto_confirmed: boolean;
}

export interface OrderStatus {
  order_id: string;
  status: PayStatus;
  amount: number;
  plan_label: string;
  protocol: Protocol | null;
  created_at: string;
  subscription_id: number | null;
}

export interface SubscriptionConfig {
  subscription_id: number;
  protocol: Protocol;
  filename: string;
  content: string;
  link: string | null;
  qr_png_base64: string;
  note?: string | null;
}

export interface TelegramAuthPayload {
  id: number;
  auth_date: number;
  hash: string;
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
}

// ---------- API ----------

export const api = {
  auth: {
    register: (email: string, password: string, name?: string) =>
      request<AuthOut>('/api/auth/register', { method: 'POST', body: JSON.stringify({ email, password, name }) }, { auth: false }),
    login: (email: string, password: string) =>
      request<AuthOut>('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }, { auth: false }),
    telegram: (payload: TelegramAuthPayload) =>
      request<AuthOut>('/api/auth/telegram', { method: 'POST', body: JSON.stringify(payload) }, { auth: false }),
    logout: () => request<{ ok: boolean }>('/api/auth/logout', { method: 'POST' }),
  },
  plans: { list: () => request<Plan[]>('/api/plans', {}, { auth: false }) },
  me: { get: () => request<User>('/api/me') },
  subs: {
    list: () => request<Subscription[]>('/api/subscriptions'),
    getConfig: (id: number) => request<SubscriptionConfig>(`/api/subscriptions/${id}/config`),
    reissue: (id: number) =>
      request<SubscriptionConfig>(`/api/subscriptions/${id}/reissue`, { method: 'POST' }),
    /**
     * Triggers a browser download of the given subscription's config.
     * Runs through the authed request layer so JWT is included automatically.
     */
    download: async (id: number) => {
      const cfg = await request<SubscriptionConfig>(`/api/subscriptions/${id}/config`);
      const blob = new Blob([cfg.content], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = cfg.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      return cfg;
    },
  },
  payments: {
    list: () => request<Payment[]>('/api/payments'),
    create: (body: CreateOrderIn) =>
      request<CreateOrderOut>('/api/payments/create', { method: 'POST', body: JSON.stringify(body) }),
    get: (orderId: string) => request<OrderStatus>(`/api/payments/${encodeURIComponent(orderId)}`),
  },
  referral: { get: () => request<ReferralStats>('/api/referral') },
};
